import { Mail, MailCheck } from 'lucide-react';
import { authLinkClass } from '../AuthControls';

type SignupConfirmationProps = { firstName: string; email: string; onBack: () => void };

/**
 * What the visitor sees once the account exists. The entrance is staggered
 * (0 / 140 / 240 / 340 / 440 / 520ms) and disabled under reduced motion by the
 * .pt-msg-in and .pt-pop rules.
 *
 * It has no "Resend the email" and no "Use a different email", which the design
 * draws. There is no endpoint to resend a verification email (signing up again
 * answers "already exists"), and by this point the account has been created for
 * this address, so "different email" would leave an unverified account behind and
 * start a second one. Both come back when the API has something to call.
 */
export const SignupConfirmation = ({ firstName, email, onBack }: SignupConfirmationProps) => {
  const step = (delay: number) => ({ animationDelay: `${delay}ms` });

  return (
    <div className="mx-auto flex w-full max-w-[560px] flex-1 flex-col items-center justify-center gap-4 text-center">
      <span
        className="pt-pop bg-panel text-accent flex h-[68px] w-[68px] items-center justify-center rounded-full"
        style={step(0)}
      >
        <MailCheck size={28} strokeWidth={1.5} aria-hidden="true" />
      </span>

      <h2
        className="pt-msg-in text-foreground m-0 text-[clamp(34px,4vw,50px)] leading-[1.04] font-medium tracking-[-0.036em]"
        style={step(140)}
      >
        Thanks, {firstName}.
      </h2>

      <p className="pt-msg-in text-dim m-0 max-w-[42ch] text-title leading-[1.6]" style={step(240)}>
        Confirm your email address to finish setting up your account. Open the link in the email to continue.
      </p>

      <div className="pt-msg-in flex flex-col items-center gap-2" style={step(340)}>
        <span className="micro-label">Sent to</span>
        <span className="bg-panel text-foreground inline-flex min-h-[42px] items-center gap-2 rounded-full px-[18px] font-mono text-lede">
          <Mail size={15} strokeWidth={1.5} className="text-accent" aria-hidden="true" />
          {email}
        </span>
      </div>

      <p className="pt-msg-in text-dim m-0 text-micro" style={step(440)}>
        Not there after a few minutes? Check your spam folder.
      </p>

      <button type="button" onClick={onBack} className={`pt-msg-in ${authLinkClass} font-medium`} style={step(520)}>
        Back to sign in
      </button>
    </div>
  );
};
